/**
 * ProviderStatusCard - External provider status display
 *
 * Features:
 * - Provider name and status indicator
 * - Latency display
 * - 24h error count
 * - Success rate percentage
 * - Last check timestamp
 */
import { cn } from '@respectly/ui';
import { Card, CardContent, CardHeader, CardTitle } from '@respectly/ui';
import { motion } from 'framer-motion';
import {
  MessageSquare,
  Mail,
  Mic2,
  CreditCard,
  Cloud,
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import type { ProviderStatus, HealthStatus } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface ProviderStatusCardProps {
  providers: ProviderStatus[];
  isLoading?: boolean;
  className?: string;
}

// ============================================
// Constants
// ============================================

const PROVIDER_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
  sinch: { icon: MessageSquare, color: 'from-blue-500 to-cyan-600' },
  mailgun: { icon: Mail, color: 'from-red-500 to-orange-600' },
  ultravox: { icon: Mic2, color: 'from-violet-500 to-purple-600' },
  stripe: { icon: CreditCard, color: 'from-indigo-500 to-blue-600' },
};

const STATUS_CONFIG: Record<HealthStatus, { icon: React.ElementType; color: string; label: string }> = {
  healthy: {
    icon: CheckCircle2,
    color: 'text-emerald-600 dark:text-emerald-400',
    label: 'Operational',
  },
  degraded: {
    icon: AlertTriangle,
    color: 'text-amber-600 dark:text-amber-400',
    label: 'Degraded',
  },
  down: {
    icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    label: 'Outage',
  },
};

// ============================================
// Helpers
// ============================================

function formatLatency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

function formatSuccessRate(rate: number | null | undefined): string {
  if (rate === null || rate === undefined) return '—';
  return `${rate.toFixed(1)}%`;
}

function formatLastCheck(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - new Date(date).getTime();
  const minutes = Math.floor(diff / (1000 * 60));

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  return new Date(date).toLocaleTimeString();
}

// ============================================
// Component
// ============================================

export function ProviderStatusCard({
  providers,
  isLoading = false,
  className,
}: ProviderStatusCardProps) {
  const healthyCount = providers.filter((p) => p.status === 'healthy').length;
  const issueCount = providers.length - healthyCount;

  return (
    <Card className={cn('border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900', className)}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Cloud className="h-4.5 w-4.5 text-white" />
            </div>
            <div>
              <CardTitle className="text-base">External Providers</CardTitle>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Third-party service integrations
              </p>
            </div>
          </div>

          {/* Summary */}
          {!isLoading && providers.length > 0 && (
            <div className="flex items-center gap-2">
              {issueCount > 0 ? (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-50 dark:bg-amber-900/20">
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                  <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                    {issueCount} issue{issueCount > 1 ? 's' : ''}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-emerald-900/20">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">
                    All operational
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {isLoading ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 animate-pulse"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-10 w-10 rounded-lg bg-slate-200 dark:bg-slate-700" />
                  <div>
                    <div className="h-4 w-20 bg-slate-200 dark:bg-slate-700 rounded mb-1" />
                    <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded" />
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                  <div className="h-4 w-12 bg-slate-200 dark:bg-slate-700 rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : providers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <div className="h-12 w-12 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-3">
              <Cloud className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              No providers configured
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {providers.map((provider, index) => {
              const config = PROVIDER_CONFIG[provider.name] || {
                icon: Cloud,
                color: 'from-slate-500 to-slate-600',
              };
              const statusConfig = STATUS_CONFIG[provider.status];
              const StatusIcon = statusConfig.icon;
              const ProviderIcon = config.icon;

              return (
                <motion.div
                  key={provider.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={cn(
                    'p-4 rounded-lg border',
                    'bg-slate-50 dark:bg-slate-800/50',
                    provider.status === 'healthy' && 'border-transparent',
                    provider.status === 'degraded' && 'border-amber-200 dark:border-amber-800/50',
                    provider.status === 'down' && 'border-red-200 dark:border-red-800/50'
                  )}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div
                        className={cn(
                          'h-10 w-10 rounded-lg flex items-center justify-center bg-gradient-to-br',
                          config.color
                        )}
                      >
                        <ProviderIcon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm text-slate-900 dark:text-white">
                          {provider.displayName}
                        </p>
                        <div className="flex items-center gap-1">
                          <StatusIcon className={cn('h-3.5 w-3.5', statusConfig.color)} />
                          <span className={cn('text-xs font-medium', statusConfig.color)}>
                            {statusConfig.label}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Metrics */}
                  <div className="flex items-center gap-4 text-xs">
                    {/* Latency */}
                    <div className="flex items-center gap-1.5">
                      <Activity className="h-3.5 w-3.5 text-slate-400" />
                      <span className="text-slate-600 dark:text-slate-400">
                        {formatLatency(provider.latencyMs)}
                      </span>
                    </div>

                    {/* Success Rate */}
                    {provider.successRate24h !== null && provider.successRate24h !== undefined && (
                      <div className="flex items-center gap-1.5">
                        <CheckCircle2
                          className={cn(
                            'h-3.5 w-3.5',
                            provider.successRate24h >= 99
                              ? 'text-emerald-500'
                              : provider.successRate24h >= 95
                                ? 'text-amber-500'
                                : 'text-red-500'
                          )}
                        />
                        <span className="text-slate-600 dark:text-slate-400">
                          {formatSuccessRate(provider.successRate24h)}
                        </span>
                      </div>
                    )}

                    {/* Error Count */}
                    {provider.errorCount24h > 0 && (
                      <div className="flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                        <span className="text-red-600 dark:text-red-400">
                          {provider.errorCount24h} errors
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Last Error */}
                  {provider.lastError && provider.status !== 'healthy' && (
                    <p className="mt-2 text-xs text-red-600 dark:text-red-400 truncate">
                      {provider.lastError}
                    </p>
                  )}

                  {/* Last Check */}
                  <p className="mt-2 text-xs text-slate-400">
                    Checked {formatLastCheck(provider.lastCheck)}
                  </p>
                </motion.div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
